import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { FontAwesomeIconProps } from 'atoms/icons';
import { FontAwesomeIcon } from 'atoms/icons';
import { Vertical } from 'atoms/layout/flex';
import { HeadlineS, LabelS } from 'atoms/typography';
import { Row } from 'components/seo/ui';

import type { Props as TransferProps } from './Transfer';
import { Transfer } from './Transfer';

const SectionTitle = styled(LabelS).attrs({
  as: 'p',
  bold: true,
  uppercase: true,
})`
  white-space: nowrap;
`;

type Props<T extends string> = {
  stats: TransferProps<T>['stats'];
  icons: Record<T, FontAwesomeIconProps['icon']>;
  nationalTeamMemberships: TransferProps<T>['transfer'][];
  clubMemberships: TransferProps<T>['transfer'][];
};

export const CareerPath = <T extends string>({
  stats,
  icons,
  nationalTeamMemberships,
  clubMemberships,
}: Props<T>) => {
  return (
    <Vertical gap={2}>
      <HeadlineS as="h2">
        <FormattedMessage
          id="Seo.Transfers.careerPath"
          defaultMessage="Career"
        />
      </HeadlineS>

      <Vertical gap={3}>
        <Vertical>
          <Row
            style={{
              gridTemplateColumns: `var(--triple-unit) 1fr repeat(${stats.length}, var(--quadruple-unit))`,
            }}
          >
            <SectionTitle color="var(--c-nd-600)">
              <FormattedMessage id="Seo.Transfers.club" defaultMessage="Club" />
            </SectionTitle>
            <span />
            {stats.map(stat => (
              <LabelS as="p" key={stat} color="var(--c-nd-600)">
                <FontAwesomeIcon icon={icons[stat]} />
              </LabelS>
            ))}
          </Row>
          {clubMemberships
            .sort((a, b) => b.startDate.getTime() - a.startDate.getTime())
            .map(transfer => (
              <Transfer key={transfer.id} transfer={transfer} stats={stats} />
            ))}
        </Vertical>
        {!!nationalTeamMemberships.length && (
          <Vertical>
            <Row
              style={{
                gridTemplateColumns: `var(--triple-unit) 1fr repeat(${stats.length}, var(--quadruple-unit))`,
              }}
            >
              <SectionTitle color="var(--c-nd-600)">
                <FormattedMessage
                  id="Seo.Transfers.nationalTeam"
                  defaultMessage="National Team"
                />
              </SectionTitle>
              <span />
            </Row>
            {nationalTeamMemberships
              .sort((a, b) => b.startDate.getTime() - a.startDate.getTime())
              .map(transfer => (
                <Transfer key={transfer.id} transfer={transfer} stats={stats} />
              ))}
          </Vertical>
        )}
      </Vertical>
    </Vertical>
  );
};
